import type { EscalationDelay } from '@/lib/api/generated/model';
import { displayNameOf } from '@/shared/components/accountIdentity';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { formatRelativeTime } from '@/shared/utils/time';
import { cn } from '@/lib/utils';

import { ADMIN_PAGE_SECTION } from '../adminLayout';
import { CONSOLE_ANSWER_COPY as ANSWER } from '../consoleCopy';
import { ConsoleAnswer, ConsoleHeader } from '../components/ConsoleHeader';
import {
  useNotificationPolicy,
  useSetEscalationDelay,
} from '../hooks/useNotificationPolicy';

const DELAYS: { value: EscalationDelay; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: '1h', label: '1 hour' },
  { value: '4h', label: '4 hours' },
  { value: '24h', label: '24 hours' },
];

const DELAY_LABEL = Object.fromEntries(
  DELAYS.map(({ value, label }) => [value, label]),
) as Record<EscalationDelay, string>;

/** An audit entry's value, which the contract types as any string: its label
 * when it is a delay this page knows, else as recorded. */
function delayLabel(value: string): string {
  return Object.hasOwn(DELAY_LABEL, value)
    ? DELAY_LABEL[value as EscalationDelay]
    : value;
}

/**
 * The organization's one notification policy (spec 138 US3): how long
 * something that needs action may sit unacknowledged before someone else is
 * told, and every change to that, newest first. Nothing about delivery,
 * channels or providers lives here, by design.
 */
export function AdminNotificationsPage() {
  const policy = useNotificationPolicy();
  const setDelay = useSetEscalationDelay();
  const showPlaceholder = usePendingDisplay(policy.isPending);
  const current = policy.data?.escalation_delay;

  return (
    <section className={ADMIN_PAGE_SECTION}>
      <ConsoleHeader
        title="Notifications"
        description="What happens when something that needs action is left alone."
        answer={
          current ? (
            <ConsoleAnswer
              figure={DELAY_LABEL[current]}
              words={
                current === 'off' ? ANSWER.escalationOff : ANSWER.escalationOn
              }
            />
          ) : undefined
        }
      />
      {policy.isPending ? (
        showPlaceholder ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : null
      ) : policy.data === undefined ? (
        <p className="text-muted-foreground text-sm">
          The notification policy could not be read just now. Refreshing may
          help.
        </p>
      ) : (
        <div className="flex max-w-2xl flex-col gap-8">
          <fieldset className="flex flex-col gap-3">
            <legend className="text-foreground mb-1 text-sm font-medium">
              Tell the workspace owner when something that needs action is left
              unacknowledged for
            </legend>
            <div
              role="radiogroup"
              className="flex flex-wrap gap-2 max-md:flex-nowrap max-md:[&>*]:flex-1"
            >
              {DELAYS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={current === value}
                  disabled={setDelay.isPending}
                  onClick={() => {
                    if (value !== current) setDelay.mutate(value);
                  }}
                  className={cn(
                    'focus-visible:ring-ring rounded-md border px-3 py-1.5 text-sm max-md:px-2 transition-colors pointer-coarse:min-h-11 focus-visible:ring-3 focus-visible:outline-none disabled:opacity-60',
                    current === value
                      ? 'border-primary bg-primary/10 text-foreground font-medium'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-muted-foreground text-xs">
              If the owner was already told, every organization admin is told
              instead. Only notifications created after a change follow it.
            </p>
          </fieldset>

          <section
            aria-labelledby="policy-history"
            className="flex flex-col gap-2"
          >
            <h2
              id="policy-history"
              className="text-foreground text-sm font-medium"
            >
              History
            </h2>
            {policy.data.history.length === 0 ? (
              <p className="text-muted-foreground text-sm">No changes yet.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {policy.data.history.map((entry) => (
                  <li
                    key={`${entry.changed_at}-${entry.after}`}
                    className="text-sm"
                  >
                    <span className="text-foreground">
                      {entry.actor
                        ? displayNameOf(
                            entry.actor.display_name,
                            entry.actor.email,
                          )
                        : 'Someone'}{' '}
                      changed it from {delayLabel(entry.before)} to{' '}
                      {delayLabel(entry.after)}
                    </span>
                    <span className="text-muted-foreground">
                      {' '}
                      · {formatRelativeTime(entry.changed_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </section>
  );
}
